import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isIosNativeOnly, isNativeNotWeb } from '@/utils/platform-check';
import { reapStaleOtaBundles, reportOtaProblem } from '@/utils/ota';

/**
 * The Me page's "check for updates" icon: ask Capgo for this device's latest
 * live update, download it, and restart into it.
 *
 * Additive only. `autoUpdate` in capacitor.config.json still checks, downloads
 * and installs on its own (see the note at the top of ota.js), so a bundle that
 * ships without this file cannot strand anyone. This is for the member who, for
 * whatever reason, has not received an update the rest of the fleet has.
 *
 * The member asked for the update by tapping, so a found bundle is installed
 * straight away: `set()` replaces the running JS, and the next boot shows the
 * existing "Updating" splash (useOtaUpdateGate). Nothing after `set()` runs.
 *
 * Never throws: every failure resolves to FAILED and is reported to RUM.
 */
export const OTA_CHECK_RESULT = Object.freeze({
  UNSUPPORTED: 'unsupported',
  UP_TO_DATE: 'up-to-date',
  INSTALLING: 'installing',
  FAILED: 'failed',
});

/** Capgo's answer when the device already has the channel's latest bundle. */
const NO_NEW_VERSION = 'no_new_version_available';

/** Plugin statuses of a bundle on disk that can be installed as it is. */
const INSTALLABLE_STATUSES = new Set(['success', 'pending']);

/**
 * Capgo answers "nothing for you" in two shapes: `kind: 'up_to_date'`, and
 * `kind: 'blocked'` for a device it will not serve (a binary below the OTA
 * floor, a channel that refuses this build). Both read as up to date to the
 * member; only the second is worth a report, because it is the reason a
 * device is not updating.
 */
function offersNothing(latest) {
  return Boolean(latest?.kind || latest?.error || !latest?.url);
}

function isPlainUpToDate(latest) {
  return latest?.kind === 'up_to_date' || latest?.error === NO_NEW_VERSION;
}

async function runningVersion() {
  try {
    return (await CapacitorUpdater.current())?.bundle?.version ?? null;
  } catch {
    return null;
  }
}

/** How long to wait for a download the plugin already has running. */
const RUNNING_DOWNLOAD_WAIT_MS = 120000;

/** How often to re-read the bundle list while waiting, in case an event was missed. */
const RUNNING_DOWNLOAD_POLL_MS = 1000;

/**
 * This version's copies on disk: one ready to install (e.g. the launch check
 * fetched it), and one the plugin is still downloading.
 */
async function findCopies(version) {
  let bundles = [];
  try {
    ({ bundles = [] } = (await CapacitorUpdater.list()) ?? {});
  } catch {
    return {};
  }
  const copies = bundles.filter(b => b?.version === version);
  return {
    ready: copies.find(b => INSTALLABLE_STATUSES.has(b.status)),
    running: copies.find(b => b.status === 'downloading'),
  };
}

/**
 * Wait for the download the plugin itself started for `version` — its own
 * foreground check can be fetching this very bundle when the member taps.
 * Starting a second download of it used to fail the tap, while the plugin's
 * copy landed and applied on the next launch.
 *
 * The plugin's events are not retained, so the bundle list is re-read as well.
 */
function waitForRunningDownload(version, onProgress) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let poll = null;
    let timer = null;
    const handles = [];

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      handles.forEach(handle => handle?.remove?.());
      fn(value);
    };
    const onBundle = event => {
      if (event?.bundle?.version === version) settle(resolve, event.bundle);
    };

    Promise.all([
      CapacitorUpdater.addListener('downloadComplete', onBundle),
      CapacitorUpdater.addListener('updateAvailable', onBundle),
      CapacitorUpdater.addListener('downloadFailed', event => {
        if (event?.version === version) {
          settle(reject, new Error('The download already running for this update failed'));
        }
      }),
      CapacitorUpdater.addListener('download', event => {
        if (event?.bundle?.version === version && Number.isFinite(event?.percent)) {
          onProgress(event.percent);
        }
      }),
    ])
      .then(added => {
        handles.push(...added);
        if (settled) added.forEach(handle => handle?.remove?.());
      })
      .catch(() => {});

    poll = setInterval(async () => {
      const { ready, running } = await findCopies(version);
      if (ready) settle(resolve, ready);
      else if (!running) {
        settle(reject, new Error('The download already running for this update stopped'));
      }
    }, RUNNING_DOWNLOAD_POLL_MS);
    timer = setTimeout(
      () => settle(reject, new Error('Timed out waiting for the download already running')),
      RUNNING_DOWNLOAD_WAIT_MS,
    );
  });
}

/**
 * The first plugin release whose iOS download() accepts a delta bundle.
 *
 * Before it, iOS download() with a `manifest` records the assembled bundle's
 * checksum as "" and then compares that with the zip's checksum, so it always
 * rejects "Checksum failed" (CapacitorUpdaterPlugin.swift, downloadBundle).
 * The plugin's own launch update skips that comparison for a manifest, which
 * is why a restart installed what the Me page could not. Android skips it too.
 */
const IOS_DELTA_DOWNLOAD_FIXED_IN = [6, 51, 23];

function isBefore(version, fixedIn) {
  const parts = String(version ?? '')
    .split('.')
    .map(Number);
  if (parts.length < 3 || parts.some(n => !Number.isInteger(n))) return true;
  const index = fixedIn.findIndex((n, i) => parts[i] !== n);
  return index !== -1 && parts[index] < fixedIn[index];
}

/**
 * Whether download() may be handed the delta files. Without them the plugin
 * fetches the full zip and checks it against `checksum`, which works on every
 * plugin version — so an unreadable version gets the zip.
 */
async function canDownloadDelta() {
  if (!isIosNativeOnly()) return true;
  try {
    const { version } = (await CapacitorUpdater.getPluginVersion()) ?? {};
    return !isBefore(version, IOS_DELTA_DOWNLOAD_FIXED_IN);
  } catch {
    return false;
  }
}

async function download(latest, onProgress) {
  const handle = await CapacitorUpdater.addListener('download', event => {
    if (Number.isFinite(event?.percent)) onProgress(event.percent);
  });
  try {
    const options = {
      url: latest.url,
      version: latest.version,
      sessionKey: latest.sessionKey,
      checksum: latest.checksum,
    };
    const manifest = (await canDownloadDelta()) ? { manifest: latest.manifest } : {};
    return await CapacitorUpdater.download({ ...options, ...manifest });
  } finally {
    await handle?.remove?.();
  }
}

/**
 * The bundle for `latest`, ready to install: the copy already on disk (one the
 * launch check fetched), else the download the plugin already has running for
 * it, else a fresh download. Rejects if none of those produce one.
 *
 * @param {object} latest what `getLatest()` offered
 * @param {(percent: number) => void} [onProgress] 0 at the start, then the
 *   download's progress, then 100
 */
export async function fetchOtaBundle(latest, onProgress = () => {}) {
  onProgress(0);
  const bundle = await obtainBundle(latest, onProgress);
  onProgress(100);
  return bundle;
}

async function obtainBundle(latest, onProgress) {
  const { ready, running } = await findCopies(latest.version);
  if (ready) return ready;
  if (running) return waitForRunningDownload(latest.version, onProgress);

  try {
    return await download(latest, onProgress);
  } catch (error) {
    // The plugin may have fetched the same bundle alongside ours.
    const after = await findCopies(latest.version);
    if (after.ready) return after.ready;
    if (after.running) return waitForRunningDownload(latest.version, onProgress);
    throw error;
  }
}

/**
 * Ask Capgo whether this device has a live update waiting.
 *
 * Shared by the Me page's icon and the update after sign-in
 * (ota-sign-in-update.js); `label` names the caller in RUM reports.
 * Never throws.
 *
 * @param {{ label?: string }} [options]
 * @returns {Promise<{ status: 'available'|'up-to-date'|'failed', latest?: object, reason?: string }>}
 */
export async function findOtaUpdate({ label = 'OTA manual check' } = {}) {
  // A stale record for a version makes the plugin skip downloading it.
  await reapStaleOtaBundles();

  let latest;
  try {
    latest = await CapacitorUpdater.getLatest();
  } catch (error) {
    const reason = error?.message || String(error);
    // iOS rejects getLatest() whenever Capgo answers with an error and no
    // `kind` (CapacitorUpdaterPlugin.swift normalises a missing kind to
    // "failed"), and "nothing newer" can arrive in that shape.
    if (reason === NO_NEW_VERSION) return { status: 'up-to-date' };
    reportOtaProblem(`${label} failed`, { step: 'check', reason });
    return { status: 'failed', reason };
  }

  if (offersNothing(latest)) {
    if (!isPlainUpToDate(latest)) {
      reportOtaProblem(`${label} offered nothing`, {
        kind: latest?.kind,
        reason: latest?.error,
        message: latest?.message,
      });
    }
    return { status: 'up-to-date' };
  }

  if (latest.version === (await runningVersion())) return { status: 'up-to-date' };

  return { status: 'available', latest };
}

/**
 * @param {object} [options]
 * @param {(percent: number) => void} [options.onProgress] called once a
 *   download starts (0), as it progresses, and when it completes (100).
 * @param {(failure: { step: string, reason: string }) => void} [options.onFailure]
 *   why a FAILED result failed, for a screen to show where RUM is not sent
 * @returns {Promise<string>} one of OTA_CHECK_RESULT
 */
export async function checkForOtaUpdate({ onProgress = () => {}, onFailure = () => {} } = {}) {
  if (!isNativeNotWeb()) return OTA_CHECK_RESULT.UNSUPPORTED;

  const found = await findOtaUpdate();
  if (found.status === 'failed') {
    onFailure({ step: 'check', reason: found.reason });
    return OTA_CHECK_RESULT.FAILED;
  }
  if (found.status === 'up-to-date') return OTA_CHECK_RESULT.UP_TO_DATE;

  const { latest } = found;
  try {
    const bundle = await fetchOtaBundle(latest, onProgress);
    await CapacitorUpdater.set({ id: bundle.id });
    return OTA_CHECK_RESULT.INSTALLING;
  } catch (error) {
    const reason = error?.message || String(error);
    console.warn('[OTA] Manual update failed:', reason);
    reportOtaProblem('OTA manual check failed', {
      step: 'install',
      version: latest.version,
      reason,
    });
    onFailure({ step: 'install', reason });
    return OTA_CHECK_RESULT.FAILED;
  }
}
